import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { EnterpriseSidebar } from "./EnterpriseDashboard";
import { ArrowLeft, Sprout } from "lucide-react";
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
    Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";

const VERDICT_STYLES = {
    Retain:   { text: "text-emerald-300", border: "border-emerald-500/40", bg: "bg-emerald-500/5" },
    Retrain:  { text: "text-amber-300",   border: "border-amber-500/40",   bg: "bg-amber-500/5" },
    Redeploy: { text: "text-red-300",     border: "border-red-500/40",     bg: "bg-red-500/5" },
};

const BAND_STYLES = {
    "Low":                  "text-emerald-300",
    "Moderate":             "text-teal-300",
    "Significant Exposure": "text-amber-300",
    "High Exposure":        "text-red-300",
    "Transforming":         "text-red-400",
};

const FLAG_LABELS = {
    SVP:    "Strategic Value Pattern",
    BI:     "Behavioural Isolation",
    CC:     "Coverage Concern",
    STP:    "Strategic Trajectory",
    "HOS+": "High-Order Specialisation",
    "MVO-L":"MV Over-weight (Low)",
    RoR:    "Right-of-Review filed",
};

function LayerBar({ label, value }) {
    const v = value ?? 0;
    return (
        <div data-testid={`layer-${label.toLowerCase()}`}>
            <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-gray-500 uppercase tracking-wider font-mono">{label}</span>
                <span className="text-white font-mono">{v?.toFixed?.(1) ?? "—"}</span>
            </div>
            <div className="h-1.5 bg-black border border-border/40">
                <div className="h-full bg-white/80" style={{ width: `${Math.min(100, v)}%` }} />
            </div>
        </div>
    );
}

export default function WorkerDiagnostic() {
    const { workerId } = useParams();
    const navigate = useNavigate();
    const { api } = useAuth();
    const [searchParams] = useSearchParams();
    const cycleIdParam = searchParams.get("cycle_id") || "";
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const url = cycleIdParam
                    ? `/v2/dashboard/worker/${workerId}?cycle_id=${cycleIdParam}`
                    : `/v2/dashboard/worker/${workerId}`;
                const res = await api.get(url);
                if (!cancelled) setData(res.data);
            } catch (e) {
                if (!cancelled) setError(e?.response?.data?.detail || e.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [api, workerId, cycleIdParam]);

    if (loading) {
        return (
            <div className="flex">
                <EnterpriseSidebar current="/enterprise/heatmap" />
                <main className="ml-56 flex-1 p-8 min-h-screen">
                    <div className="animate-pulse space-y-4">
                        <div className="h-8 bg-card w-1/3" />
                        <div className="h-40 bg-card" />
                    </div>
                </main>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="flex">
                <EnterpriseSidebar current="/enterprise/heatmap" />
                <main className="ml-56 flex-1 p-8 min-h-screen">
                    <button onClick={() => navigate(-1)} className="text-xs text-gray-500 hover:text-white flex items-center gap-2 mb-6">
                        <ArrowLeft className="w-3 h-3" /> Back
                    </button>
                    <div className="bg-card border border-border/40 p-6 text-red-300 text-sm">{error || "Candidate not found"}</div>
                </main>
            </div>
        );
    }

    const v = data.verdict;
    const verdictKey = v?.verdict;
    const vStyle = VERDICT_STYLES[verdictKey] || { text: "text-gray-400", border: "border-border/40", bg: "bg-black/30" };
    const cap = data.capability;
    const disp = data.displacement;
    const flags = data.advisory_flags || [];

    return (
        <div className="flex">
            <EnterpriseSidebar current="/enterprise/heatmap" />
            <main className="ml-56 flex-1 p-8 min-h-screen" data-testid="worker-diagnostic">
                <div className="max-w-6xl space-y-6">
                    <button onClick={() => navigate(-1)} className="text-xs text-gray-500 hover:text-white flex items-center gap-2" data-testid="back-btn">
                        <ArrowLeft className="w-3 h-3" /> Back
                    </button>

                    {/* Header */}
                    <div className="flex items-start justify-between gap-6">
                        <div>
                            <h1 className="text-2xl font-display font-bold text-white">{data.candidate.full_name}</h1>
                            <p className="text-sm text-gray-500 mt-1 font-body">
                                {data.candidate.job_title} · {data.candidate.department || "—"} · {data.candidate.region || "—"}
                            </p>
                            <p className="text-xs text-gray-600 mt-0.5">
                                Cycle: {data.cycle.name} · Archetype: {data.candidate.archetype || "—"} · Cadre: {cap?.cadre || "—"}
                            </p>
                        </div>
                        {v && (
                            <div className="flex items-start gap-3">
                                <PushToEquipButton verdictId={v.id} verdict={v.verdict} />
                                <div className={`px-5 py-3 border ${vStyle.border} ${vStyle.bg}`} data-testid="verdict-badge">
                                    <p className="text-xs text-gray-500 uppercase tracking-wider">Verdict</p>
                                    <p className={`text-2xl font-display font-bold ${vStyle.text}`}>{v.verdict}</p>
                                    {v.overridden && <p className="text-xs text-amber-300 mt-1">Overridden from {v.original_verdict}</p>}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Capability + Displacement side by side */}
                    <div className="grid lg:grid-cols-2 gap-6">
                        {/* Capability */}
                        <div className="bg-card border border-border/40 p-6" data-testid="capability-card">
                            <div className="flex items-baseline justify-between mb-4">
                                <h2 className="text-sm uppercase tracking-wider text-gray-500">Capability Composite</h2>
                                <span className="text-3xl font-mono font-bold text-white">
                                    {cap?.composite_final !== undefined ? cap.composite_final?.toFixed?.(1) : "—"}
                                </span>
                            </div>
                            {cap?.label && <p className="text-xs text-gray-400 mb-4">{cap.label}</p>}
                            {cap ? (
                                <div className="space-y-3">
                                    <LayerBar label="MV" value={cap.layers?.MV} />
                                    <LayerBar label="TA" value={cap.layers?.TA} />
                                    <LayerBar label="HOS" value={cap.layers?.HOS} />
                                    <LayerBar label="TSA" value={cap.layers?.TSA} />
                                </div>
                            ) : (
                                <p className="text-xs text-gray-600">No capability composite computed yet.</p>
                            )}
                            {cap?.values_floor_applied && (
                                <div className="mt-4 px-3 py-2 border border-amber-500/40 bg-amber-500/5 text-xs text-amber-300">
                                    ⚐ Values Floor applied. Unfloored composite: {cap.composite_unfloored?.toFixed?.(1)}
                                </div>
                            )}
                        </div>

                        {/* Displacement */}
                        <div className="bg-card border border-border/40 p-6" data-testid="displacement-card">
                            <div className="flex items-baseline justify-between mb-4">
                                <h2 className="text-sm uppercase tracking-wider text-gray-500">Displacement Composite</h2>
                                <span className="text-3xl font-mono font-bold text-white">
                                    {disp?.composite_final !== undefined ? disp.composite_final?.toFixed?.(1) : "—"}
                                </span>
                            </div>
                            {disp ? (
                                <>
                                    <p className={`text-xs mb-4 ${BAND_STYLES[disp.band] || "text-gray-400"}`}>{disp.band}</p>
                                    <div className="space-y-2">
                                        <p className="text-xs text-gray-500 uppercase tracking-wider mt-3 mb-1">Modifiers</p>
                                        {[
                                            { l: "Tool Exposure", v: disp.modifiers?.tool_exposure },
                                            { l: "Judgment Concentration", v: disp.modifiers?.judgment_concentration },
                                            { l: "Reskilling Adjacency", v: disp.modifiers?.reskilling_adjacency },
                                            { l: "Strategic Lock-in", v: disp.modifiers?.strategic_lockin },
                                        ].map((m) => (
                                            <div key={m.l} className="flex items-center justify-between text-xs px-3 py-1.5 border border-border/40">
                                                <span className="text-gray-400">{m.l}</span>
                                                <span className={`font-mono ${m.v > 0 ? "text-red-300" : m.v < 0 ? "text-emerald-300" : "text-gray-500"}`}>
                                                    {m.v > 0 ? "+" : ""}{(m.v ?? 0).toFixed?.(1) ?? "0"}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <p className="text-xs text-gray-600">No displacement composite computed yet.</p>
                            )}
                        </div>
                    </div>

                    {/* Verdict reasoning */}
                    {v && v.reasoning && (
                        <div className="bg-card border border-border/40 p-6" data-testid="verdict-reasoning">
                            <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Verdict Reasoning</h3>
                            <p className="text-sm text-gray-300 whitespace-pre-line font-body leading-relaxed">{v.reasoning}</p>
                            {v.overridden && v.override_rationale && (
                                <div className="mt-4 px-3 py-2 border border-amber-500/40 bg-amber-500/5 text-xs text-amber-200">
                                    <p className="font-semibold mb-1">Override Rationale</p>
                                    <p className="text-amber-100">{v.override_rationale}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Top / bottom tasks */}
                    {disp && (disp.top_displaced_tasks?.length > 0 || disp.top_resilient_tasks?.length > 0) && (
                        <div className="grid lg:grid-cols-2 gap-6">
                            <div className="bg-card border border-border/40 p-6" data-testid="top-displaced">
                                <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Top Displaced Tasks</h3>
                                <div className="space-y-2">
                                    {(disp.top_displaced_tasks || []).map((t, i) => (
                                        <div key={i} className="flex items-center justify-between text-sm border border-border/40 px-3 py-2">
                                            <span className="text-white">{t.task_name || t.task || t.name}</span>
                                            <span className="font-mono text-red-300">{(t.task_displacement ?? t.disp ?? 0).toFixed?.(1)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="bg-card border border-border/40 p-6" data-testid="top-resilient">
                                <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Top Resilient Tasks</h3>
                                <div className="space-y-2">
                                    {(disp.top_resilient_tasks || []).map((t, i) => (
                                        <div key={i} className="flex items-center justify-between text-sm border border-border/40 px-3 py-2">
                                            <span className="text-white">{t.task_name || t.task || t.name}</span>
                                            <span className="font-mono text-emerald-300">{(t.task_displacement ?? t.disp ?? 0).toFixed?.(1)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Task breakdown table */}
                    {data.tasks && data.tasks.length > 0 && (
                        <div className="bg-card border border-border/40" data-testid="task-table">
                            <div className="px-6 py-4 border-b border-border/40">
                                <h3 className="text-sm uppercase tracking-wider text-gray-500">Per-Task AS / AL / JD</h3>
                            </div>
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-gray-500 uppercase tracking-wider border-b border-border/40">
                                        <th className="text-left px-4 py-2">Task</th>
                                        <th className="text-right px-4 py-2">Time %</th>
                                        <th className="text-right px-4 py-2">AS</th>
                                        <th className="text-right px-4 py-2">AL</th>
                                        <th className="text-right px-4 py-2">JD</th>
                                        <th className="text-right px-4 py-2">Disp</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.tasks.map((t, i) => (
                                        <tr key={i} className="border-b border-border/20">
                                            <td className="px-4 py-2 text-white">{t.task_name}</td>
                                            <td className="px-4 py-2 text-right font-mono text-gray-400">{t.time_share_pct?.toFixed?.(0) ?? "—"}</td>
                                            <td className="px-4 py-2 text-right font-mono text-gray-300">{t.automation_susceptibility?.toFixed?.(0) ?? "—"}</td>
                                            <td className="px-4 py-2 text-right font-mono text-gray-300">{t.augmentation_leverage?.toFixed?.(0) ?? "—"}</td>
                                            <td className="px-4 py-2 text-right font-mono text-gray-300">{t.judgment_density?.toFixed?.(0) ?? "—"}</td>
                                            <td className="px-4 py-2 text-right font-mono text-white">{t.task_displacement?.toFixed?.(1) ?? "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Advisory flags */}
                    {flags.length > 0 && (
                        <div className="bg-card border border-border/40 p-6" data-testid="advisory-flags">
                            <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Advisory Flags</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                                {flags.map((f) => (
                                    <div key={f.flag_code}
                                        className={`p-3 border ${f.triggered ? "border-amber-500/40 bg-amber-500/5" : "border-border/40 bg-black/30"}`}
                                        data-testid={`flag-${f.flag_code}`}>
                                        <p className={`text-xs font-mono ${f.triggered ? "text-amber-300" : "text-gray-500"}`}>{f.flag_code}</p>
                                        <p className={`text-xs mt-1 ${f.triggered ? "text-amber-200" : "text-gray-500"}`}>
                                            {FLAG_LABELS[f.flag_code] || f.flag_code}
                                        </p>
                                        <p className={`text-xs mt-1 ${f.triggered ? "text-amber-300" : "text-gray-600"}`}>
                                            {f.triggered ? "Triggered" : "Considered"}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Demo extras (peer ranking, tool recs, skill gap, 30/60/90, trajectory, RoR) */}
                    {v?.extras?.peer_ranking && <PeerRankingPanel data={v.extras.peer_ranking} />}
                    {v?.extras?.tool_recommendations && <ToolRecommendationsPanel data={v.extras.tool_recommendations} />}
                    {v?.extras?.skill_gap_analysis && <SkillGapPanel data={v.extras.skill_gap_analysis} />}
                    {v?.extras?.development_plan_30_60_90 && <DevelopmentPlanPanel data={v.extras.development_plan_30_60_90} />}
                    {v?.extras?.role_formalization && <RoleFormalizationPanel data={v.extras.role_formalization} />}
                    {v?.extras?.trajectory_projection && <TrajectoryPanel data={v.extras.trajectory_projection} />}
                    <OptionalTrainingPanel candidateId={data.candidate.id} cycleId={cycleIdParam || data.cycle?.id} />
                    {data.right_of_review?.submitted && data.right_of_review?.contested_items && (
                        <RoRPanel data={data.right_of_review} />
                    )}
                </div>
            </main>
        </div>
    );
}


function PushToEquipButton({ verdictId, verdict }) {
    const { api } = useAuth();
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    if (verdict !== "Retrain" && verdict !== "Redeploy") return null;

    const push = async () => {
        setBusy(true); setError(null); setResult(null);
        try {
            const r = await api.post(`/v2/admin/equip/push/${verdictId}`);
            setResult(r.data);
        } catch (e) {
            setError(e?.response?.data?.detail || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="flex flex-col items-end gap-2">
            <button onClick={push} disabled={busy}
                className="flex items-center gap-2 px-3 py-2 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40 text-xs font-mono uppercase tracking-wider"
                data-testid="push-to-equip-btn">
                <Sprout className="w-3.5 h-3.5" />
                {busy ? "Pushing…" : "Push to Equip Arm"}
            </button>
            {result && (
                <div className="text-xs text-emerald-300 max-w-xs text-right" data-testid="push-result">
                    Enroll: {result.enroll?.proposed ?? 0} proposed · Mentors: {result.mentor?.matched ?? 0} matched · Redeploy: {result.redeployment?.matched ?? 0} matched
                </div>
            )}
            {error && <div className="text-xs text-red-300 max-w-xs text-right">{error}</div>}
        </div>
    );
}


// ---------- Peer Ranking ----------
function PeerRankingPanel({ data }) {
    const cmp = data.peer_comparison || {};
    return (
        <div className="bg-card border border-border/40 p-6" data-testid="peer-ranking-panel">
            <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-4">Where you stand · Peer Ranking</h3>
            <p className="text-xs text-gray-400 mb-4 font-body leading-relaxed">{data.rank_narrative}</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="border border-emerald-500/30 bg-emerald-500/5 p-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500">Capability rank</p>
                    <p className="text-3xl font-mono text-emerald-300 mt-1">
                        {data.capability_rank}<span className="text-base text-gray-500"> / {data.peer_group_size}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{data.capability_percentile}th percentile</p>
                </div>
                <div className="border border-red-500/30 bg-red-500/5 p-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500">Displacement rank</p>
                    <p className="text-3xl font-mono text-red-300 mt-1">
                        {data.displacement_rank}<span className="text-base text-gray-500"> / {data.peer_group_size}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{data.displacement_percentile}th percentile (most exposed)</p>
                </div>
                <div className="border border-border/40 p-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500">Overall trajectory</p>
                    <p className="text-3xl font-mono text-white mt-1">
                        {data.overall_trajectory_rank}<span className="text-base text-gray-500"> / {data.peer_group_size}</span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">peer group: {data.peer_group}</p>
                </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <div className="border border-border/40 px-3 py-2">
                    <span className="text-gray-500">Median peer capability:</span>
                    <span className="text-white font-mono ml-2">{cmp.median_peer_capability}</span>
                    <span className={`ml-2 font-mono ${cmp.sarah_delta_capability >= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        ({cmp.sarah_delta_capability >= 0 ? "+" : ""}{cmp.sarah_delta_capability})
                    </span>
                </div>
                <div className="border border-border/40 px-3 py-2">
                    <span className="text-gray-500">Median peer displacement:</span>
                    <span className="text-white font-mono ml-2">{cmp.median_peer_displacement}</span>
                    <span className={`ml-2 font-mono ${cmp.sarah_delta_displacement <= 0 ? "text-emerald-300" : "text-red-300"}`}>
                        ({cmp.sarah_delta_displacement >= 0 ? "+" : ""}{cmp.sarah_delta_displacement})
                    </span>
                </div>
            </div>
        </div>
    );
}


// ---------- Tool Recommendations ----------
function ToolRecommendationsPanel({ data }) {
    const ranked = data.ranked || [];
    const out = data.combined_projected_outcome || {};
    return (
        <div className="bg-card border border-border/40 p-6" data-testid="tool-recommendations-panel">
            <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-sm uppercase tracking-wider text-gray-500">Ranked Tool Recommendations</h3>
                <span className="text-xs text-gray-500 font-mono">
                    {data.total_recommended} tools · ~{data.total_projected_hours_reclaimed_per_week}h/week reclaimed
                </span>
            </div>
            <div className="space-y-3">
                {ranked.map((r) => (
                    <div key={r.rank} className="border border-border/40 p-4" data-testid={`tool-rec-${r.rank}`}>
                        <div className="flex items-baseline justify-between gap-3">
                            <div className="flex items-baseline gap-3">
                                <span className="text-2xl font-display font-bold text-white">#{r.rank}</span>
                                <span className="text-sm text-white font-medium">{r.tool}</span>
                            </div>
                            <span className="text-xs font-mono text-emerald-300 uppercase tracking-wider">{r.priority}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-2 font-body leading-relaxed">{r.why}</p>
                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            <div className="border border-border/40 px-2 py-1.5">
                                <p className="text-gray-500">Hrs/week</p>
                                <p className="text-emerald-300 font-mono">+{r.projected_hours_reclaimed}</p>
                            </div>
                            <div className="border border-border/40 px-2 py-1.5">
                                <p className="text-gray-500">Δ Disp</p>
                                <p className="text-emerald-300 font-mono">{r.projected_displacement_delta}</p>
                            </div>
                            <div className="border border-border/40 px-2 py-1.5">
                                <p className="text-gray-500">Cost</p>
                                <p className="text-white font-mono text-[10px]">{r.cost}</p>
                            </div>
                            <div className="border border-border/40 px-2 py-1.5">
                                <p className="text-gray-500">Ramp</p>
                                <p className="text-white font-mono">{r.onboarding_time}</p>
                            </div>
                        </div>
                        <ul className="mt-3 text-xs text-gray-400 list-disc pl-4 space-y-0.5">
                            {(r.addresses_tasks || []).map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                        <p className="text-xs text-gray-500 mt-2 italic">Success: {r.success_metric}</p>
                    </div>
                ))}
            </div>
            {data.not_recommended?.length > 0 && (
                <div className="mt-4 border-t border-border/40 pt-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Not recommended</p>
                    <div className="space-y-1.5">
                        {data.not_recommended.map((nr, i) => (
                            <div key={i} className="text-xs text-gray-400">
                                <span className="text-red-300 font-mono mr-2">×</span>
                                <span className="text-white">{nr.tool}</span>
                                <span className="text-gray-500"> — {nr.reason}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {Object.keys(out).length > 0 && (
                <div className="mt-4 border-t border-border/40 pt-4 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                    <div className="border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
                        <p className="text-gray-500 uppercase tracking-wider text-[10px]">Q3 Displacement</p>
                        <p className="text-emerald-300 font-mono text-lg">{out.new_displacement_composite_q3}</p>
                    </div>
                    <div className="border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
                        <p className="text-gray-500 uppercase tracking-wider text-[10px]">Q3 Verdict</p>
                        <p className="text-emerald-200 font-body">{out.new_verdict_q3}</p>
                    </div>
                    <div className="border border-border/40 px-3 py-2">
                        <p className="text-gray-500 uppercase tracking-wider text-[10px]">Cap growth</p>
                        <p className="text-white font-mono text-lg">+{out.capability_growth_projected}</p>
                    </div>
                </div>
            )}
        </div>
    );
}


// ---------- Skill Gap ----------
function SkillGapPanel({ data }) {
    return (
        <div className="bg-card border border-border/40 p-6" data-testid="skill-gap-panel">
            <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-4">Skill Gap Analysis</h3>

            <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Protected skills to develop</p>
            <div className="space-y-3 mb-6">
                {(data.protected_skill_areas_to_develop || []).map((s, i) => (
                    <div key={i} className="border border-border/40 p-4" data-testid={`skill-gap-${i}`}>
                        <div className="flex items-baseline justify-between gap-3">
                            <span className="text-sm text-white font-medium">{s.skill}</span>
                            <span className="text-xs font-mono text-gray-400">
                                <span className="text-red-300">{s.current_level}</span>
                                <span className="mx-1 text-gray-600">→</span>
                                <span className="text-emerald-300">{s.target_level}</span>
                                <span className="text-gray-500 ml-1">/ 5</span>
                            </span>
                        </div>
                        <div className="mt-2 flex h-1.5 bg-black border border-border/40">
                            <div className="bg-red-500/60" style={{ width: `${(s.current_level / 5) * 100}%` }} />
                            <div className="bg-emerald-500/40" style={{ width: `${((s.target_level - s.current_level) / 5) * 100}%` }} />
                        </div>
                        <p className="text-xs text-gray-400 mt-2 font-body leading-relaxed"><span className="text-gray-500">Why it protects: </span>{s.why_it_protects}</p>
                        <p className="text-xs text-gray-400 mt-1 font-body leading-relaxed"><span className="text-gray-500">Pathway: </span>{s.learning_pathway}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Current strengths</p>
                    <ul className="space-y-1.5 text-xs text-gray-400">
                        {(data.current_strength_areas || []).map((s, i) => (
                            <li key={i} className="border-l-2 border-emerald-500/40 pl-3">
                                <span className="text-white">{s.skill}</span>
                                <span className="text-gray-500"> · {s.level}/5</span>
                                <p className="text-gray-500 italic mt-0.5">{s.note}</p>
                            </li>
                        ))}
                    </ul>
                </div>
                <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Strengths to preserve</p>
                    <ul className="space-y-1.5 text-xs text-gray-400">
                        {(data.strengths_to_preserve || []).map((s, i) => (
                            <li key={i} className="border-l-2 border-border/40 pl-3 text-gray-400">{s}</li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}


// ---------- 30/60/90 Day Plan ----------
function DevelopmentPlanPanel({ data }) {
    const buckets = [
        { key: "thirty_day", label: "30 Days", items: data.thirty_day || [] },
        { key: "sixty_day", label: "60 Days", items: data.sixty_day || [] },
        { key: "ninety_day", label: "90 Days", items: data.ninety_day || [] },
    ];
    const proj = data.projected_q3_state || {};
    return (
        <div className="bg-card border border-border/40 p-6" data-testid="development-plan-panel">
            <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-sm uppercase tracking-wider text-gray-500">30 / 60 / 90 Day Development Plan</h3>
                {data.manager && <span className="text-xs text-gray-500">Manager: {data.manager}</span>}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {buckets.map((b) => (
                    <div key={b.key} className="border border-border/40 p-4" data-testid={`plan-bucket-${b.key}`}>
                        <p className="text-xs uppercase tracking-wider text-emerald-300 font-mono mb-3">{b.label}</p>
                        <ul className="space-y-3">
                            {b.items.map((item, i) => (
                                <li key={i} className="border-l-2 border-emerald-500/40 pl-3">
                                    <p className="text-sm text-white font-body">{item.action}</p>
                                    <p className="text-xs text-gray-500 mt-0.5 font-mono uppercase tracking-wider">{item.type}</p>
                                    <p className="text-xs text-gray-400 mt-1"><span className="text-gray-500">Owner: </span>{item.owner}</p>
                                    <p className="text-xs text-gray-400 italic mt-1">{item.success_metric}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            {Object.keys(proj).length > 0 && (
                <div className="mt-5 border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="projected-q3-state">
                    <p className="text-xs uppercase tracking-wider text-emerald-300 font-mono mb-2">Projected Q3 State</p>
                    <p className="text-sm text-white font-body mb-2">{proj.verdict}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div><span className="text-gray-500">Band: </span><span className="text-white">{proj.band}</span></div>
                        <div><span className="text-gray-500">Cap rank: </span><span className="text-white">{proj.peer_rank_capability}</span></div>
                        <div><span className="text-gray-500">Disp rank: </span><span className="text-white">{proj.peer_rank_displacement}</span></div>
                        <div><span className="text-gray-500">Trajectory: </span><span className="text-white">{proj.overall_trajectory}</span></div>
                    </div>
                    {proj.role_evolution && (
                        <p className="text-xs text-emerald-200 mt-2 font-body italic">→ {proj.role_evolution}</p>
                    )}
                </div>
            )}

            {data.manager_actions_required?.length > 0 && (
                <div className="mt-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Manager actions required</p>
                    <ul className="text-xs text-gray-400 space-y-1 list-disc pl-5">
                        {data.manager_actions_required.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}


// ---------- Optional Suggested Training ----------
function OptionalTrainingPanel({ candidateId, cycleId }) {
    const { api } = useAuth();
    const [recs, setRecs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/v2/training/recommendations/${candidateId}?cycle_id=${cycleId}`);
                if (!cancelled) setRecs(r.data.recommendations || []);
            } catch (e) { /* no-op */ }
            finally { if (!cancelled) setLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [api, candidateId, cycleId]);

    const markInterested = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/v2/training/recommendations/${id}/interest`, { expressed_via: "self_service" });
            const r = await api.get(`/v2/training/recommendations/${candidateId}?cycle_id=${cycleId}`);
            setRecs(r.data.recommendations || []);
        } catch (e) { /* no-op */ }
        finally { setBusyId(null); }
    };

    if (loading) return null;

    return (
        <div className="bg-card border border-border/40 p-6" data-testid="optional-training-panel">
            <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-1">Optional Suggested Training</h3>
            <p className="text-xs text-gray-400 mb-5 font-body leading-relaxed">
                These are aspirational growth recommendations — not requirements. Take any, all, or none.
                They're designed to extend your capability runway and prepare you for higher-leverage work.
            </p>

            {recs.length === 0 ? (
                <div className="border border-border/40 p-6 text-center">
                    <p className="text-sm text-gray-500">No training recommendations have been generated for this candidate yet.</p>
                    <p className="text-xs text-gray-600 mt-1">Your manager can request curated suggestions through the Equip arm.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {recs.map((r) => {
                        const sig = r.interest_signal;
                        return (
                            <div key={r.id} className="border border-border/40 p-4" data-testid={`training-rec-${r.display_order}`}>
                                <div className="flex items-baseline justify-between gap-3">
                                    <div>
                                        <p className="text-sm text-white font-display font-semibold">{r.title}</p>
                                        <p className="text-xs text-gray-500 font-mono mt-0.5">{r.provider}</p>
                                    </div>
                                    {sig ? (
                                        <span className={`text-xs font-mono uppercase tracking-wider px-2 py-1 border ${
                                            sig.enrollment_status === "enrolled"
                                                ? "border-white/40 text-white bg-white/5"
                                                : "border-gray-600 text-gray-400 bg-gray-500/5"
                                        }`} data-testid={`training-status-${r.display_order}`}>
                                            {sig.enrollment_status}
                                        </span>
                                    ) : (
                                        <button onClick={() => markInterested(r.id)} disabled={busyId === r.id}
                                            className="text-xs font-mono uppercase tracking-wider px-3 py-1 border border-border/40 text-gray-300 hover:text-white hover:border-white/40 disabled:opacity-40 transition-colors"
                                            data-testid={`training-interest-btn-${r.display_order}`}>
                                            {busyId === r.id ? "Saving…" : "Mark as interested"}
                                        </button>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3 text-xs">
                                    <div className="border border-border/40 px-2 py-1.5">
                                        <p className="text-gray-500">Format</p>
                                        <p className="text-white font-mono text-[11px]">{r.format}</p>
                                    </div>
                                    <div className="border border-border/40 px-2 py-1.5">
                                        <p className="text-gray-500">Time</p>
                                        <p className="text-white font-mono text-[11px]">{r.time_commitment}</p>
                                    </div>
                                    <div className="border border-border/40 px-2 py-1.5">
                                        <p className="text-gray-500">Cost</p>
                                        <p className="text-white font-mono text-[11px]">{r.cost_display}</p>
                                    </div>
                                </div>
                                <p className="text-xs text-gray-400 mt-3 font-body leading-relaxed">
                                    <span className="text-gray-500">Why: </span>{r.why_suggested}
                                </p>
                                <p className="text-xs text-gray-300 mt-2 font-body italic">→ {r.aspirational_outcome}</p>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}


// ---------- Role Formalization (Maya — title alignment instead of dev plan) ----------
function RoleFormalizationPanel({ data }) {
    return (
        <div className="bg-card border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="role-formalization-panel">
            <div className="flex items-baseline justify-between mb-4">
                <h3 className="text-sm uppercase tracking-wider text-emerald-300">Role Formalization Recommendation</h3>
                <span className="text-xs font-mono text-emerald-200">title alignment — not skill growth</span>
            </div>
            <div className="flex items-center gap-4 mb-4">
                <div className="flex-1 border border-border/40 bg-black/30 p-4">
                    <p className="text-xs uppercase tracking-wider text-gray-500">Current title</p>
                    <p className="text-lg text-white font-display mt-1">{data.current_title}</p>
                </div>
                <span className="text-2xl text-emerald-300 font-mono">→</span>
                <div className="flex-1 border border-emerald-500/40 bg-emerald-500/10 p-4">
                    <p className="text-xs uppercase tracking-wider text-emerald-300">Recommended title</p>
                    <p className="text-lg text-emerald-200 font-display mt-1">{data.recommended_title}</p>
                </div>
            </div>
            <p className="text-sm text-gray-300 font-body leading-relaxed mb-4">{data.rationale}</p>
            {data.manager_actions_required?.length > 0 && (
                <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Manager actions required</p>
                    <ul className="text-xs text-gray-400 space-y-1 list-disc pl-5">
                        {data.manager_actions_required.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}


// ---------- Trajectory Chart ----------
function TrajectoryPanel({ data }) {
    const merged = (data.with_plan || []).map((wp, i) => {
        const wo = (data.without_plan || [])[i] || {};
        return {
            cycle: wp.cycle.replace(" (today)", "").replace(/^Q(\d) (\d{4}).*/, "Q$1 '$2"),
            with_plan_cap: wp.capability,
            with_plan_disp: wp.displacement,
            without_plan_disp: wo.displacement,
            without_plan_cap: wo.capability,
        };
    });

    return (
        <div className="bg-card border border-border/40 p-6" data-testid="trajectory-panel">
            <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-1">Trajectory Projection · Two Futures</h3>
            <p className="text-xs text-gray-400 mb-4 font-body leading-relaxed">{data.the_choice_narrative}</p>
            <div className="h-72 -ml-2" data-testid="trajectory-chart">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={merged} margin={{ top: 10, right: 24, bottom: 10, left: 0 }}>
                        <CartesianGrid stroke="#1f1f1f" strokeDasharray="3 3" />
                        <XAxis dataKey="cycle" stroke="#666" tick={{ fontSize: 11, fill: "#888" }} />
                        <YAxis domain={[0, 100]} stroke="#666" tick={{ fontSize: 11, fill: "#888" }}
                            label={{ value: "Score", angle: -90, position: "insideLeft", fill: "#666", fontSize: 11 }} />
                        <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #333", fontSize: 12 }}
                            labelStyle={{ color: "#fff" }} itemStyle={{ color: "#ddd" }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <ReferenceLine y={50} stroke="#444" strokeDasharray="2 4" />
                        <Line type="monotone" dataKey="with_plan_cap" name="Capability (with plan)"
                            stroke="#34d399" strokeWidth={2.5} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="with_plan_disp" name="Displacement (with plan)"
                            stroke="#60a5fa" strokeWidth={2.5} dot={{ r: 4 }} />
                        <Line type="monotone" dataKey="without_plan_disp" name="Displacement (no action)"
                            stroke="#f87171" strokeWidth={2} strokeDasharray="6 4" dot={{ r: 3 }} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div className="border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="trajectory-with-plan">
                    <p className="text-emerald-300 font-mono uppercase tracking-wider mb-1">With plan executed</p>
                    {(data.with_plan || []).slice(-1).map((p, i) => (
                        <p key={i} className="text-gray-300 font-body">{p.cycle}: {p.verdict} · Cap {p.capability} · Disp {p.displacement}</p>
                    ))}
                </div>
                <div className="border border-red-500/30 bg-red-500/5 p-3" data-testid="trajectory-without-plan">
                    <p className="text-red-300 font-mono uppercase tracking-wider mb-1">Without action</p>
                    {(data.without_plan || []).slice(-1).map((p, i) => (
                        <p key={i} className="text-gray-300 font-body">{p.cycle}: {p.verdict} · Cap {p.capability} · Disp {p.displacement}</p>
                    ))}
                </div>
            </div>
        </div>
    );
}


// ---------- Right-of-Review (contested items) ----------
function RoRPanel({ data }) {
    return (
        <div className="bg-card border border-amber-500/30 bg-amber-500/5 p-6" data-testid="ror-panel">
            <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-sm uppercase tracking-wider text-amber-300">Right-of-Review · Submission</h3>
                <span className="text-xs font-mono text-amber-200">{data.contested_count} contested · status: {data.status}</span>
            </div>
            {data.overall_narrative && (
                <p className="text-sm text-amber-100 mb-4 font-body leading-relaxed italic">"{data.overall_narrative}"</p>
            )}
            <div className="space-y-2">
                {(data.contested_items || []).map((it, i) => (
                    <div key={i} className="border border-amber-500/20 bg-black/30 p-3" data-testid={`ror-item-${i}`}>
                        <div className="flex items-baseline justify-between gap-3 text-xs">
                            <span className="text-white font-medium">{it.task || it.field}</span>
                            <span className="font-mono text-gray-400">
                                <span className="text-red-300">{String(it.platform_value ?? it.contested_value)}</span>
                                <span className="mx-1 text-gray-600">vs</span>
                                <span className="text-emerald-300">{String(it.candidate_value ?? it.sarah_claimed_value)}</span>
                            </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1.5 font-body leading-relaxed">{it.rationale}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}
